import type { ListModulePaginationMeta } from '../components/list-module/list-module.types';

/** Safe string for list search/filter — avoids `[object Object]` from String(object). */
export function stringifyUnknownFieldValue(value: unknown, fallback = ''): string {
  if (value == null) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => stringifyUnknownFieldValue(item))
      .filter(Boolean)
      .join(', ');
  }
  return fallback;
}

/** Client-side search across stringified field values. */
export function filterRecordsBySearch<T extends Record<string, unknown>>(
  records: T[],
  query: string,
  keys: string[],
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return records;
  return records.filter((row) =>
    keys.some((key) => stringifyUnknownFieldValue(row[key]).toLowerCase().includes(q)),
  );
}

/** Client-side status filter for enum or boolean status fields. */
export function filterRecordsByStatus<T extends Record<string, unknown>>(
  records: T[],
  status: string,
  statusField: string | null,
): T[] {
  if (!status?.trim() || !statusField) return records;
  const normalized = status.trim().toLowerCase();
  return records.filter((row) => {
    const value = row[statusField];
    if (typeof value === 'boolean') {
      if (normalized === 'active') return value === true;
      if (normalized === 'inactive') return value === false;
      return true;
    }
    return stringifyUnknownFieldValue(value).toLowerCase() === normalized;
  });
}

export function paginateClientRecords<T>(
  records: T[],
  page: number,
  perPage: number,
): { pageRows: T[]; meta: ListModulePaginationMeta } {
  const total = records.length;
  const lastPage = Math.max(1, Math.ceil(total / perPage) || 1);
  const currentPage = Math.min(Math.max(1, page), lastPage);
  const start = (currentPage - 1) * perPage;
  return {
    pageRows: records.slice(start, start + perPage),
    meta: { total, lastPage, currentPage, perPage },
  };
}
